// Relative articulation measure — a pure leaf (no engine state), unit-tested.
// Articulation (legato/staccato) is the held *fraction* of each note vs. the
// written value: legato ≈ held to the next onset (ratio near 1), staccato ≈ short.
// Comparing the player's hold-fraction to the expected one judges the *pattern*
// (did you detach the staccato notes, sustain the legato ones?) and is tempo-proof
// because it is a ratio. See …-expression-coach-design.md §4.4.

/** One judged note's hold data, all times in the same unit (seconds). */
interface HeldNote {
  /** How long the player actually held the key. */
  playerHeld: number;
  /** The note's written/sounding duration. */
  expectedHeld: number;
  /** Time from this onset to the next (the legato ceiling); ≤0 ⇒ skip. */
  interOnset: number;
}

/**
 * Relative articulation, 0..1, averaged over a span. Per note, the closeness of the
 * player's hold-fraction (`playerHeld / interOnset`) to the expected hold-fraction
 * (`expectedHeld / interOnset`). Notes with no following onset are skipped; an empty
 * (or all-skipped) span scores `1` (nothing to grade).
 */
export function relativeArticulation(notes: readonly HeldNote[]): number {
  let sum = 0;
  let count = 0;
  for (const { playerHeld, expectedHeld, interOnset } of notes) {
    if (interOnset <= 0) continue;
    const playerFrac = playerHeld / interOnset;
    const expectedFrac = expectedHeld / interOnset;
    sum += 1 - Math.min(1, Math.abs(playerFrac - expectedFrac));
    count += 1;
  }
  return count === 0 ? 1 : sum / count;
}
